/**
 * Funnel AI Service
 * Provides AI-powered assistance for funnel building
 */

// Analyze current canvas state and generate contextual suggestions
export const analyzeCanvasState = (nodes, edges) => {
    const suggestions = [];
    const campaigns = nodes.filter(n => n.type === 'campaign');
    const adsets = nodes.filter(n => n.type === 'adset');
    const ads = nodes.filter(n => n.type === 'ad');
    const abtests = nodes.filter(n => n.type === 'abtest');
    const pools = nodes.filter(n => n.type === 'retargetingPool');
    const segments = nodes.filter(n => n.type === 'segment');

    // Empty canvas
    if (nodes.length === 0) {
        suggestions.push({
            type: 'action',
            priority: 1,
            message: 'Start by adding a Campaign node. This is the foundation of your funnel.',
            action: 'add_campaign'
        });
        return suggestions;
    }

    // Has campaigns but no ad sets
    const campaignsWithoutAdSets = campaigns.filter(c =>
        !edges.some(e => e.source === c.id && adsets.some(a => a.id === e.target))
    );
    if (campaignsWithoutAdSets.length > 0) {
        suggestions.push({
            type: 'action',
            priority: 2,
            message: `Campaign "${campaignsWithoutAdSets[0].data.name}" needs an Ad Set to define targeting.`,
            action: 'add_adset'
        });
    }

    // Has ad sets but no ads
    const adsetsWithoutAds = adsets.filter(a =>
        !edges.some(e => e.source === a.id && ads.some(ad => ad.id === e.target))
    );
    if (adsetsWithoutAds.length > 0) {
        suggestions.push({
            type: 'action',
            priority: 3,
            message: `Ad Set "${adsetsWithoutAds[0].data.name}" needs ads to show to your audience.`,
            action: 'add_ad'
        });
    }

    // Budget recommendations
    const noBudgetCampaigns = campaigns.filter(c =>
        !c.data.budget?.amount || c.data.budget.amount <= 0
    );
    if (noBudgetCampaigns.length > 0) {
        suggestions.push({
            type: 'warning',
            priority: 2,
            message: `Set a budget for "${noBudgetCampaigns[0].data.name}" to control your ad spend.`,
            action: 'edit_campaign'
        });
    }

    // Funnel stage recommendations
    const stages = campaigns.map(c => c.data.funnelStage);
    if (stages.includes('TOFU') && !stages.includes('MOFU') && !stages.includes('BOFU')) {
        suggestions.push({
            type: 'tip',
            priority: 4,
            message: 'Consider adding MOFU or BOFU campaigns to convert your aware audience.',
            action: null
        });
    }

    // Multi-stage funnel without retargeting pools
    const hasMultiStage = (stages.includes('TOFU') && stages.includes('MOFU')) ||
                          (stages.includes('MOFU') && stages.includes('BOFU'));
    if (hasMultiStage && pools.length === 0) {
        suggestions.push({
            type: 'tip',
            priority: 4,
            message: 'Add a Retargeting Pool to automatically move engaged users between funnel stages.',
            action: 'add_retargetingPool'
        });
    }

    // BOFU campaigns without high-intent segments
    const hasBofu = stages.includes('BOFU');
    if (hasBofu && segments.length === 0) {
        suggestions.push({
            type: 'tip',
            priority: 5,
            message: 'Create an Audience Segment to target high-intent users for your conversion campaigns.',
            action: 'add_segment'
        });
    }

    // A/B testing recommendation
    if (ads.length >= 2 && abtests.length === 0) {
        suggestions.push({
            type: 'tip',
            priority: 5,
            message: 'Try A/B testing your ads to find the best performing creative.',
            action: 'add_abtest'
        });
    }

    return suggestions.sort((a, b) => a.priority - b.priority);
};

// Generate quick prompts based on context
export const getQuickPrompts = (nodes, edges) => {
    const prompts = [];

    if (nodes.length === 0) {
        prompts.push('What makes a good awareness campaign?');
        prompts.push('How do I structure a marketing funnel?');
        prompts.push('What budget should I start with?');
    } else {
        const campaigns = nodes.filter(n => n.type === 'campaign');
        const adsets = nodes.filter(n => n.type === 'adset');
        const ads = nodes.filter(n => n.type === 'ad');
        const pools = nodes.filter(n => n.type === 'retargetingPool');
        const segments = nodes.filter(n => n.type === 'segment');
        const stages = campaigns.map(c => c.data.funnelStage);

        if (campaigns.length > 0 && adsets.length === 0) {
            prompts.push('How do I define my target audience?');
            prompts.push('What targeting options are available?');
        }

        if (adsets.length > 0 && ads.length === 0) {
            prompts.push('What makes an effective ad?');
            prompts.push('How do I write compelling ad copy?');
        }

        // Multi-stage funnel prompts
        const hasMultiStage = (stages.includes('TOFU') && stages.includes('MOFU')) ||
                              (stages.includes('MOFU') && stages.includes('BOFU'));
        if (hasMultiStage && pools.length === 0) {
            prompts.push('What is a retargeting pool?');
            prompts.push('How do I connect funnel stages?');
        }

        // BOFU without segments
        if (stages.includes('BOFU') && segments.length === 0) {
            prompts.push('What is an audience segment?');
            prompts.push('How do I target high-intent users?');
        }

        // Has pools or segments - offer comparison
        if (pools.length > 0 || segments.length > 0) {
            prompts.push('What\'s the difference between segment and pool?');
        }

        if (ads.length > 0) {
            prompts.push('How do I optimize my ads?');
            prompts.push('When should I use A/B testing?');
            prompts.push('How do I measure campaign success?');
        }

        prompts.push('What should I do next?');
    }

    return prompts.slice(0, 4);
};

// Pre-defined responses for common questions (can be extended with actual AI)
const FAQ_RESPONSES = {
    'what makes a good awareness campaign':
        'A good awareness campaign focuses on reaching new people who haven\'t heard of your brand. Key elements include:\n\n• **Broad targeting** - Cast a wide net to reach potential customers\n• **Engaging visuals** - Video content typically performs best\n• **Clear brand message** - Focus on who you are, not hard selling\n• **Reasonable budget** - Start with $20-50/day to gather data',

    'how do i structure a marketing funnel':
        'A marketing funnel has three main stages:\n\n1. **TOFU (Top of Funnel)** - Awareness\n   Build brand recognition with new audiences\n\n2. **MOFU (Middle of Funnel)** - Consideration\n   Nurture interested users with valuable content\n\n3. **BOFU (Bottom of Funnel)** - Conversion\n   Convert warm leads into customers with offers',

    'what budget should i start with':
        'For beginners, I recommend:\n\n• **Testing phase**: $20-50/day per campaign\n• **Minimum viable data**: Run for at least 7 days\n• **Total monthly**: $500-1500 for meaningful results\n\nStart small, analyze results, then scale what works!',

    'how do i define my target audience':
        'Define your audience by considering:\n\n• **Demographics** - Age, gender, location\n• **Interests** - What they like and follow\n• **Behaviors** - Purchase habits, device usage\n• **Lookalikes** - Similar to existing customers\n\nStart broad and narrow down based on performance data.',

    'what makes an effective ad':
        'Effective ads have:\n\n• **Hook** - Grab attention in first 3 seconds\n• **Value proposition** - Clear benefit to the viewer\n• **Social proof** - Reviews, testimonials, results\n• **Strong CTA** - Tell them exactly what to do\n• **Mobile-first** - Design for small screens',

    'what should i do next':
        'Based on your current funnel, here are suggested next steps:\n\n1. Complete any unfinished nodes (campaigns without ad sets, ad sets without ads)\n2. Set budgets for all campaigns\n3. Add creatives to your ads\n4. Consider A/B testing different ad variations\n5. Save your funnel and push to your ad platform',

    'retargeting pool':
        'A **Retargeting Pool** automatically captures users who engaged with your ads at one funnel stage and makes them available for targeting at the next stage.\n\n**Example:** Users who watched 50%+ of your TOFU video are automatically added to a pool that feeds your MOFU campaigns.\n\n**Without pools:** You manually create audiences in Meta/Google.\n**With pools:** The funnel becomes an automated system that moves users through stages based on their engagement.',

    'audience segment':
        'An **Audience Segment** defines WHO to target based on intent signals:\n\n• **Engagement Depth** - Ad views, video watch %, clicks\n• **Website Behavior** - Pages visited, cart activity, time on site\n• **Recency** - How recently they interacted\n• **Stage Progression** - Users who moved through your funnel\n\nUse segments to target your highest-intent users for conversion campaigns.',

    'connect funnel stages':
        'To create an automated funnel flow:\n\n1. **Add a Retargeting Pool** between stages\n2. **Connect** your TOFU campaign TO the pool (users flow in)\n3. **Connect** the pool TO your MOFU ad set (users flow out)\n\nThe pool captures engaged users and feeds them to the next stage automatically.\n\n**Tip:** Use different pool types for different behaviors:\n• **Engagers** - Any ad interaction\n• **Video Viewers** - Watched X% of video\n• **Website Visitors** - Visited your site',

    'difference between segment and pool':
        '**Retargeting Pools** and **Audience Segments** serve different purposes:\n\n**Retargeting Pool:**\n• Captures users based on simple triggers (views, clicks, visits)\n• Automatically flows users between funnel stages\n• Think of it as a "bucket" that collects engaged users\n\n**Audience Segment:**\n• Filters users based on multiple intent signals\n• Defines WHO to target (high-intent vs low-intent)\n• Think of it as a "filter" for your most valuable users\n\n**Use both together:** Pools move users between stages, segments target the best users within each stage.'
};

// Get AI response for a question (simplified version)
export const getAIResponse = async (question, context) => {
    // Normalize the question
    const normalizedQuestion = question.toLowerCase().trim();

    // Check for FAQ matches
    for (const [key, response] of Object.entries(FAQ_RESPONSES)) {
        if (normalizedQuestion.includes(key) || key.includes(normalizedQuestion)) {
            return response;
        }
    }

    // Generate context-aware response
    const suggestions = analyzeCanvasState(context.nodes || [], context.edges || []);

    if (normalizedQuestion.includes('next') || normalizedQuestion.includes('should i')) {
        if (suggestions.length > 0) {
            return `Based on your current funnel, I suggest: ${suggestions[0].message}\n\n${FAQ_RESPONSES['what should i do next']}`;
        }
    }

    // Default response
    return `I can help you with:\n\n• Creating effective advertising funnels\n• Understanding funnel stages (TOFU, MOFU, BOFU)\n• Targeting and audience selection\n• Budget recommendations\n• Ad creative best practices\n\nTry asking about any of these topics!`;
};

export default {
    analyzeCanvasState,
    getQuickPrompts,
    getAIResponse
};
